import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import logoMark from "@/assets/smg-logo-mark.png";

const UPDATED = "October 2026";

const LEGAL_PATHS = {
  privacy: "/privacy",
  terms: "/terms",
  accessibility: "/accessibility",
  social: "/social-responsibility",
  "human-rights": "/human-rights",
  "us-notice": "/us-privacy-notice",
  "intl-notice": "/client-privacy-notice",
};

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    intro:
      "SMG Sports Management Group International (a division of SMG Media Group, Carlsbad, California) respects your privacy. This policy explains what we collect through this website, why we collect it, and the choices you have.",
    sections: [
      {
        h: "Information we collect",
        bullets: [
          "Inquiry information you submit through our athlete application or brand partnership forms — such as your name, email address, phone number, location, sport details, rankings, social handles, statements and any links you choose to share.",
          "Cookie preferences stored in your browser when you make a cookie choice.",
          "Basic technical information your browser sends when visiting the site.",
        ],
      },
      {
        h: "How we use information",
        bullets: [
          "To respond to athlete applications and brand partnership inquiries.",
          "To evaluate potential representation and partnership opportunities.",
          "To communicate with you about opportunities you have asked us to consider.",
          "To operate, secure and improve the website.",
        ],
      },
      {
        h: "Sharing",
        ps: [
          "We do not sell your personal information. We share inquiry details only within SMG Sports Management Group International and SMG Media Group, and with service providers who help us operate the site and send notifications — under instructions and appropriate safeguards.",
        ],
      },
      {
        h: "Youth athletes",
        ps: [
          "Inquiries on behalf of athletes under 18 must be submitted by a parent or legal guardian. We do not knowingly collect information directly from children.",
        ],
      },
      {
        h: "Retention & security",
        ps: [
          "We keep inquiry information only as long as needed to evaluate and act on your request, or as required by law. We use reasonable administrative and technical safeguards, but no method of transmission over the internet is completely secure.",
        ],
      },
      {
        h: "Your choices",
        ps: [
          "You may request access to, correction of, or deletion of the information you submitted by emailing brian@smgmediagroup.com. You can revisit your cookie choices at any time through the Cookie Settings link in the footer.",
        ],
      },
      {
        h: "Contact",
        ps: [
          "SMG Sports Management Group International · Carlsbad, CA · brian@smgmediagroup.com · +1 (619) 552-4677.",
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    intro:
      "Welcome to the SMG Sports Management Group International website. By using this site, you agree to these terms. If you do not agree, please do not use the site.",
    sections: [
      {
        h: "About this site",
        ps: [
          "This site presents SMG Sports Management Group International, a pickleball-focused athlete representation agency and a division of SMG Media Group. It allows athletes to apply for representation and brands to inquire about partnership opportunities.",
        ],
      },
      {
        h: "No guarantee of representation or outcomes",
        bullets: [
          "Submitting an application or inquiry does not create an agent-athlete or client relationship. Representation is subject to approval.",
          "SMG makes no guarantee of sponsorships, earnings, scholarships, rankings, playing time, or professional status.",
          "Nothing on this site is legal, financial or tax advice.",
        ],
      },
      {
        h: "Intellectual property",
        ps: [
          "The SMG name, logos, and all site content are owned by SMG Media Group and protected by trademark, copyright and other laws. The SMG™ mark is used as an unregistered trademark. You may not copy, reuse or republish site content without written permission.",
        ],
      },
      {
        h: "Acceptable use",
        ps: [
          "Do not submit false, misleading or unauthorized information; attempt to gain unauthorized access to the site or its systems; use the site to send unlawful or infringing content; or scrape or abuse the site or its forms.",
        ],
      },
      {
        h: "Youth submissions",
        ps: [
          "Applications for athletes under 18 must be made by a parent or legal guardian who consents to these terms on the athlete's behalf.",
        ],
      },
      {
        h: "Limitation of liability",
        ps: [
          "The site is provided 'as is' without warranties of any kind. To the fullest extent permitted by law, SMG is not liable for indirect or consequential damages arising from use of the site.",
        ],
      },
      {
        h: "Governing law & changes",
        ps: [
          "These terms are governed by the laws of the State of California. We may update these terms from time to time; the current version will always be posted here with its revision date.",
        ],
      },
    ],
  },
  accessibility: {
    title: "Accessibility Statement",
    intro:
      "SMG Sports Management Group International is committed to making this website usable by everyone, including athletes, families and partners with disabilities.",
    sections: [
      {
        h: "Our commitment",
        bullets: [
          "We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA.",
          "We design with readable text sizes, visible focus states, sufficient color contrast and full keyboard operability.",
          "We test the site on desktop and mobile devices during development.",
        ],
      },
      {
        h: "Known limitations",
        ps: [
          "Some historical media or third-party embedded content may not yet meet every guideline. We address gaps as we find them.",
        ],
      },
      {
        h: "Feedback",
        ps: [
          "If you encounter an accessibility barrier, email brian@smgmediagroup.com or call +1 (619) 552-4677 and we will work with you to provide the information or transaction you need through an alternative method.",
        ],
      },
    ],
  },
  social: {
    title: "Social Responsibility",
    intro:
      "Sport only works when it lifts people up. As an agency built on the fastest-growing community sport in the world, SMG Sports Management Group International holds itself to standards that go beyond deals.",
    sections: [
      {
        h: "Youth sport safety",
        ps: [
          "Every youth-athlete inquiry requires a parent or legal guardian. We are committed to responsible guidance, age-appropriate opportunities, and protecting young athletes' wellbeing, education and long-term interests above short-term gains.",
        ],
      },
      {
        h: "Growing the game responsibly",
        ps: [
          "We support the healthy, inclusive growth of pickleball at community, amateur and professional levels, and we favor partners and programs that share that commitment.",
        ],
      },
      {
        h: "Honest representation",
        ps: [
          "We do not promise outcomes we cannot control. Our commitments to athletes and brands are stated plainly, honored consistently, and reviewed continuously.",
        ],
      },
      {
        h: "Accountability",
        ps: [
          "Questions or concerns about our conduct can be directed to brian@smgmediagroup.com. We take every report seriously.",
        ],
      },
    ],
  },
  "human-rights": {
    title: "Human Rights",
    intro:
      "SMG Sports Management Group International is committed to respecting internationally recognized human rights across our own operations and the partners, suppliers and events we work with.",
    sections: [
      {
        h: "Our commitments",
        bullets: [
          "No forced labor, bonded labor or child labor anywhere in our operations or by our direct partners.",
          "Non-discrimination and equal opportunity regardless of race, color, religion, sex, sexual orientation, gender identity, national origin, age, disability or any other protected characteristic.",
          "Fair dealing with athletes, families, staff, contractors and brand partners.",
          "A safe, respectful and harassment-free working environment at events, activations and engagements we control.",
        ],
      },
      {
        h: "Athlete welfare",
        ps: [
          "The wellbeing of the athletes we represent comes before any commercial opportunity. For athletes under 18, a parent or legal guardian must be involved in every step.",
        ],
      },
      {
        h: "Raising concerns",
        ps: [
          "If you believe a person or organization connected with SMG is involved in a human rights concern, contact brian@smgmediagroup.com. We will review and act on good-faith reports.",
        ],
      },
    ],
  },
  "us-notice": {
    title: "US Client Privacy Notice",
    intro:
      "This notice applies to residents of the United States (including California) and describes how SMG Sports Management Group International handles personal information submitted through this website, in accordance with applicable U.S. state privacy laws.",
    sections: [
      {
        h: "Categories of personal information we collect",
        bullets: [
          "Identifiers and contact details: name, email address, phone number, city and state.",
          "Professional and athlete information: sport, competitive level, rankings, social media handles, websites, highlights and statements you choose to provide.",
          "Commercial-inquiry information for brands: company, partnership interests, audience and budget details.",
          "Internet activity information: cookie preferences and basic technical data.",
        ],
      },
      {
        h: "Purposes",
        ps: [
          "We use this information to respond to inquiries, evaluate representation and partnership opportunities, communicate with you, and operate and secure the website.",
        ],
      },
      {
        h: "Sale or sharing of personal information",
        ps: [
          "We do not sell or share personal information for cross-context behavioral advertising as defined by state privacy laws.",
        ],
      },
      {
        h: "Your rights",
        bullets: [
          "Right to know what personal information we hold about you.",
          "Right to request deletion or correction.",
          "Right to non-discrimination for exercising your privacy rights.",
        ],
      },
      {
        h: "How to exercise rights",
        ps: [
          "Email brian@smgmediagroup.com with your request. We will verify your identity as required and respond within the timeframes required by law. This notice was last updated in " + UPDATED + ".",
        ],
      },
    ],
  },
  "intl-notice": {
    title: "Client Privacy Notice (excluding US)",
    intro:
      "For individuals outside the United States, this notice explains how SMG Sports Management Group International (a division of SMG Media Group) processes personal information in line with applicable data protection laws, including the GDPR where it applies.",
    sections: [
      {
        h: "Data controller",
        ps: [
          "SMG Sports Management Group International, Carlsbad, California, USA is the controller of personal information submitted through this website. Contact: brian@smgmediagroup.com.",
        ],
      },
      {
        h: "What we process & why",
        bullets: [
          "Inquiry data (name, contact details, athlete or brand information) — to respond to your request and evaluate opportunities — processed on the basis of your consent and our legitimate interests.",
          "Cookie preference data — based on your consent, which you can withdraw at any time via Cookie Settings.",
        ],
      },
      {
        h: "International transfers",
        ps: [
          "Information you submit is processed in the United States. Where required, we rely on appropriate safeguards for transfers of personal data.",
        ],
      },
      {
        h: "Retention",
        ps: [
          "We retain inquiry information only as long as necessary to evaluate and act on your request, or to comply with legal obligations.",
        ],
      },
      {
        h: "Your rights",
        bullets: [
          "Access, rectification, erasure, restriction of processing, data portability, and objection to processing.",
          "You may withdraw consent at any time without affecting prior processing.",
          "You may lodge a complaint with your local supervisory authority.",
        ],
      },
      {
        h: "Contact",
        ps: ["Email brian@smgmediagroup.com to exercise any of these rights."],
      },
    ],
  },
};

function LegalPage({ docKey }) {
  const doc = DOCS[docKey];
  const path = LEGAL_PATHS[docKey];

  useEffect(() => {
    if (!doc || !path) return;
    document.title = `${doc.title} | SMG Sports Management`;
    const desc = doc.intro.length > 160 ? doc.intro.slice(0, 157).trimEnd() + "..." : doc.intro;
    let el = document.querySelector('meta[name="description"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "description");
      document.head.appendChild(el);
    }
    el.setAttribute("content", desc);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", `https://smgsportsmgt.com${path}`);
  }, [doc, path]);

  if (!doc) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink text-white">
        <p>Page not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-slate-50">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink/90 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-4xl items-center justify-between px-6 py-3">
          <Link to="/" data-testid="legal-logo-link">
            <span className="relative inline-block">
              <img src={logoMark} alt="SMG Sports Management" className="h-10 w-auto" />
              <span className="absolute -right-3 -top-1 font-mono text-[9px] text-slate-400">™</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-wider text-slate-300 transition-colors hover:border-volt/50 hover:text-volt"
          >
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="relative mx-auto w-full max-w-4xl px-6 py-16">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt" data-testid="legal-doc-label">
          {doc.title}
        </p>
        <h1 className="mt-3 font-display text-4xl uppercase text-white sm:text-5xl" data-testid="legal-doc-title">
          {doc.title}
        </h1>
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
          Last updated: {UPDATED}
        </p>
        <p className="mt-8 text-base leading-relaxed text-slate-300">{doc.intro}</p>

        <div className="mt-12 space-y-10">
          {doc.sections.map((s) => (
            <section key={s.h} data-testid="legal-doc-section">
              <h2 className="font-display text-xl uppercase tracking-wide text-white">{s.h}</h2>
              {s.ps &&
                s.ps.map((p, i) => (
                  <p key={i} className="mt-3 text-sm leading-relaxed text-slate-300">
                    {p}
                  </p>
                ))}
              {s.bullets && (
                <ul className="mt-3 space-y-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-volt" />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </main>

      <footer className="border-t border-white/10">
        <div className="spectrum-rule" aria-hidden="true" />
        <div className="mx-auto w-full max-w-4xl px-6 py-8 text-[11px] text-slate-500">
          SMG™ is a trademark of SMG Media Group. This page is provided for general information and does not constitute legal advice.
        </div>
      </footer>
    </div>
  );
}

export default LegalPage;
